import { useState, useEffect } from "react";
import { useParams, Link } from "@tanstack/react-router";
import type { SourceConfig, SourceSnapshot } from "@tuit/shared-types";
import { SourceConfigTable } from "../components/admin/SourceConfigTable";
import { SourceTable } from "../components/admin/SourceTable";
import { SectionView } from "../components/SectionView";
import { SourcePicturesView } from "../components/SourcePicturesView";
import { SourceGalleryView } from "../components/SourceGalleryView";
import adminStyles from "../styles/components/AdminPage.module.css";
import styles from "../styles/pages/SourcePage.module.css";

const EMPTY_SET = new Set<string>();
const EMPTY_MAP = new Map<string, string[]>();
const noop = () => {};
const noopRef = () => {};

export function SourcePage() {
  const { sourceId } = useParams({ from: "/source/$sourceId" });
  const [config, setConfig] = useState<SourceConfig | null>(null);
  const [snapshot, setSnapshot] = useState<SourceSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [sourceStyle, setSourceStyle] = useState<"posts" | "pictures" | "gallery">("posts");

  useEffect(() => {
    setConfig(null);
    setSnapshot(null);
    setLoading(true);
    Promise.all([
      fetch(`/api/source/${sourceId}/config`).then((r) => r.json()),
      fetch(`/api/source/${sourceId}/snapshot`).then((r) => r.json()),
    ]).then(([cfg, snap]) => {
      setConfig(cfg as SourceConfig);
      setSnapshot(snap as SourceSnapshot);
    }).finally(() => setLoading(false));
  }, [sourceId]);

  async function rebuildSnapshot() {
    setRebuilding(true);
    setSnapshot(null);
    try {
      await fetch(`/api/source/${sourceId}/snapshot`, { method: "DELETE" });
      const snap = await fetch(`/api/source/${sourceId}/snapshot`).then((r) => r.json());
      setSnapshot(snap as SourceSnapshot);
    } finally {
      setRebuilding(false);
    }
  }

  return (
    <div className={styles.page}>
      <nav className={styles.topbar}>
        <Link to="/admin" className={styles.back}>← Admin</Link>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Source Editor <span className={styles.titleSep}>›</span> {sourceId}</h1>
          <button
            type="button"
            className={styles.rebuildBtn}
            onClick={rebuildSnapshot}
            disabled={rebuilding || loading}
          >
            {rebuilding ? "Rebuilding…" : "Rebuild Snapshot"}
          </button>
        </div>
      </nav>
      <div className={adminStyles.sourceRow}>
        <div className={adminStyles.panelGroup}>
          <SourceConfigTable
            config={config}
            sourceId={sourceId}
            loading={loading}
            onSaved={setConfig}
          />
          {snapshot && (
            <div className={styles.previewPanel}>
              <div className={styles.previewHeader}>
                <span className={styles.previewLabel}>Source Style</span>
                <div className={styles.styleToggle}>
                  {(["posts", "pictures", "gallery"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`${styles.styleBtn} ${sourceStyle === s ? styles.styleBtnActive : ""}`}
                      onClick={() => setSourceStyle(s)}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {sourceStyle === "posts" && (
                <SectionView
                  section={snapshot}
                  readIds={EMPTY_SET}
                  onRead={noop}
                  suppressed={EMPTY_SET}
                  alsoIn={EMPTY_MAP}
                  sectionRef={noopRef}
                />
              )}
              {sourceStyle === "pictures" && <SourcePicturesView snapshot={snapshot} />}
              {sourceStyle === "gallery" && <SourceGalleryView snapshot={snapshot} />}
            </div>
          )}
        </div>
        <div className={adminStyles.panelGroup}>
          <SourceTable snapshot={snapshot} sourceId={sourceId} loading={loading} />
        </div>
      </div>
    </div>
  );
}
